import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { format, addWeeks, addMonths, addQuarters, isBefore, startOfToday } from "date-fns";
import {
  CalendarIcon,
  Loader2,
  RefreshCw,
  Check,
  ChevronsUpDown,
  Clock,
  User,
  Video,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/contexts/LocationContext";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatPhoneDisplay, getTimezoneForState, getNowInTimezone } from "@/lib/utils";
import AuthService from "@/lib/auth.ts";
import { v4 as uuidv4 } from "uuid";
import { useAppointmentTypes } from "@/hooks/useAppointmentTypes";
import { Building2, MapPin } from "lucide-react";

const RECURRENCE_TYPES = {
  NONE: "none",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
} as const;

type RecurrenceType = (typeof RECURRENCE_TYPES)[keyof typeof RECURRENCE_TYPES];

const APPOINTMENT_REASONS = [
  { value: "Initial",                  label: "Initial" },
  { value: "Initial Therapy",          label: "Initial Therapy" },
  { value: "Follow-up",                label: "Follow-up" },
  { value: "Internal care transfer",   label: "Internal care transfer" },
] as const;

type AppointmentReason = (typeof APPOINTMENT_REASONS)[number]["value"];

const FOLLOWUP_DURATIONS = [
  { value: 20, label: "20 minutes" },
  { value: 40, label: "40 minutes" },
  { value: 60, label: "60 minutes" },
] as const;

function buildVisitReason(
  patientType: "Adult" | "Non-Adult" | null,
  reason: AppointmentReason | "",
  duration?: number | null,
): string {
  if (!reason) return "";
  const pt = patientType ?? "Adult";
  switch (reason) {
    case "Initial":
      return "Therapeutic Intake Call Appointment";
    case "Initial Therapy":
      return duration
        ? `${pt} Therapy - F/U ${duration}`
        : `${pt} Therapy - Intake`;
    case "Follow-up":
      return duration ? `${pt} - F/U ${duration}` : `${pt} - F/U`;
    case "Internal care transfer":
      return "Internal Trans. of Care";
    default:
      return reason;
  }
}

function derivePatientType(dateOfBirth: string | null | undefined): "Adult" | "Non-Adult" | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear()
    - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
  return age >= 18 ? "Adult" : "Non-Adult";
}

let appointmentTypeEnum: [string, ...string[]] = ["TeleHealth", "InPerson", "RemoteTheropyMonitoring", "SMS_Survey"];

const createAppointmentFormSchema = (types: string[]) => {
  if (types.length > 0) {
    appointmentTypeEnum = types as [string, ...string[]];
  }
  return z
    .object({
      patientId:           z.string().min(1, "Please select a patient"),
      date:                z.date({ required_error: "Appointment date is required" }),
      time:                z.string().min(1, "Appointment time is required"),
      type:                z.enum(appointmentTypeEnum, { required_error: "Please select an appointment type" }),
      reason:              z.string().min(1, "Please select an appointment reason"),
      followUpDuration:    z.number().optional().nullable(),
      hospitalName:        z.string().min(1, "Hospital is required"),
      locationId:          z.string().min(1, "Location is required"),
      notes:               z.string().optional(),
      isRecurring:         z.boolean().default(false),
      recurrenceType:      z.nativeEnum(RECURRENCE_TYPES).default(RECURRENCE_TYPES.WEEKLY),
      recurrenceEndDate:   z.date().optional(),
    })
    .refine(
      (data) => { if (data.isRecurring) return !!data.recurrenceEndDate; return true; },
      { message: "End date is required for recurring appointments", path: ["recurrenceEndDate"] },
    )
    .refine(
      (data) => {
        if (data.reason === "Follow-up" || data.reason === "Initial Therapy") return !!data.followUpDuration;
        return true;
      },
      { message: "Please select a duration", path: ["followUpDuration"] },
    )
    .refine(
      (data) => {
        return data.date > startOfToday();
      },
      { message: "Appointments cannot be scheduled for today. Please select tomorrow or a future date.", path: ["date"] },
    );
};

type Patient = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  mobile_number: string | null;
  date_of_birth?: string | null;
};

interface AddAppointmentFormProps {
  onSuccess?: () => void;
  defaultPatientId?: string;
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

export function AddAppointmentForm({ onSuccess, defaultPatientId }: AddAppointmentFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);

  const { appointmentTypes } = useAppointmentTypes();
  const { selectedLocationId, locations } = useLocation();

  const fetchPatients = async (search = "") => {
    try {
      setIsSearching(true);
      const res = await AuthService.getPatientsWithFilters({
        search,
        location_id: selectedLocationId ? String(selectedLocationId) : undefined,
      });
      setPatients(res?.data || res || []);
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setIsLoadingPatients(false);
      setIsSearching(false);
    }
  };

  const fetchLocations = async () => {
    try {
      await AuthService.getHospitalsAndBranches();
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  useEffect(() => {
    fetchPatients();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (!defaultPatientId || isLoadingPatients) return;
    if (patients.find(p => p.id === defaultPatientId)) {
      form.setValue("patientId", defaultPatientId, { shouldValidate: true });
    } else {
      AuthService.getPatient(defaultPatientId).then((p: any) => {
        if (p) {
          setPatients(prev => prev.find(x => x.id === p.id) ? prev : [p, ...prev]);
          form.setValue("patientId", p.id, { shouldValidate: true });
        }
      }).catch(() => {});
    }
  }, [defaultPatientId, isLoadingPatients]);

  useEffect(() => {
    if (selectedLocationId !== undefined) fetchPatients(searchQuery);
  }, [selectedLocationId]);

  useEffect(() => {
    const timer = setTimeout(() => fetchPatients(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formSchema = createAppointmentFormSchema(appointmentTypes.map((t) => t.value));

  type FormValues = {
    patientId: string;
    date: Date;
    time: string;
    type: string;
    reason: string;
    followUpDuration?: number | null;
    hospitalName: string;
    locationId: string;
    notes?: string;
    isRecurring: boolean;
    recurrenceType: RecurrenceType;
    recurrenceEndDate?: Date;
  };

  // Compute initial date/time once at mount using clinic timezone if already
  // available, otherwise fall back to browser local. useRef makes this stable —
  // useForm defaultValues only apply on first mount, never again on re-render.
  const initialDefaults = useRef((() => {
    let tz = "America/New_York";
    if (selectedLocationId && locations.length > 0) {
      const loc = locations.find(l => l.value === selectedLocationId);
      if (loc?.stateName) tz = getTimezoneForState(loc.stateName);
    }
    const now = getNowInTimezone(tz);
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const nextHour = (now.getHours() + 1) % 24;
    const time = `${String(nextHour).padStart(2, "0")}:00`;
    return { date, time };
  })());

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      patientId:         "",
      date:              initialDefaults.current.date,
      time:              initialDefaults.current.time,
      type:              "TeleHealth",
      reason:            "",
      followUpDuration:  null,
      hospitalName:      "",
      locationId:        selectedLocationId ? String(selectedLocationId) : "",
      notes:             "",
      isRecurring:       false,
      recurrenceType:    RECURRENCE_TYPES.WEEKLY,
    },
  });

  useEffect(() => {
    if (selectedLocationId && !form.getValues("locationId")) {
      form.setValue("locationId", String(selectedLocationId));
    }
  }, [selectedLocationId, form]);

  // This ref tracks whether we have already corrected the date/time defaults
  // after locations finished loading. It ensures we only attempt this once.
  const defaultsApplied = useRef(false);

  useEffect(() => {
    // Only run once. Once locations have loaded and we have applied the
    // correct timezone-aware defaults, we never touch date/time again.
    if (defaultsApplied.current) return;
    if (!selectedLocationId || locations.length === 0) return;

    const loc = locations.find(l => l.value === selectedLocationId);
    if (!loc?.stateName) return;

    const tz = getTimezoneForState(loc.stateName);
    const now = getNowInTimezone(tz);
    const correctDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const nextHour = (now.getHours() + 1) % 24;
    const correctTime = `${String(nextHour).padStart(2, "0")}:00`;

    // Only update each field if the clinician has not manually changed it.
    // We detect "not manually changed" by comparing the current form value
    // against what initialDefaults computed at mount time.
    // If they still match, the clinician has not touched them — safe to correct.
    const currentDate = form.getValues("date");
    const currentTime = form.getValues("time");

    if (currentDate?.getTime() === initialDefaults.current.date?.getTime()) {
      form.setValue("date", correctDate);
    }
    if (currentTime === initialDefaults.current.time) {
      form.setValue("time", correctTime);
    }

    defaultsApplied.current = true;
  }, [locations, selectedLocationId]);

  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);
  const hospitals = Array.from(new Set(locations.map(l => l.hospitalName))).sort();

  const handleHospitalChange = (hospitalName: string) => {
    form.setValue("hospitalName", hospitalName);
    const filtered = locations.filter(l => l.hospitalName === hospitalName);
    setFilteredLocations(filtered);
    form.setValue("locationId", "");
  };

  useEffect(() => {
    const selectedHospital = form.watch("hospitalName");
    if (selectedHospital && locations.length > 0) {
      const filtered = locations.filter(l => l.hospitalName === selectedHospital);
      setFilteredLocations(filtered);
    }
  }, [locations, form.watch("hospitalName")]);

  useEffect(() => {
    const selectedLocId = form.watch("locationId");
    if (selectedLocId && locations.length > 0 && !form.watch("hospitalName")) {
      const loc = locations.find(l => String(l.value) === selectedLocId);
      if (loc?.hospitalName) form.setValue("hospitalName", loc.hospitalName);
    }
  }, [locations, form.watch("locationId"), form.watch("hospitalName"), form.setValue]);

  const selectedPatientId  = form.watch("patientId");
  const selectedReason     = form.watch("reason") as AppointmentReason | "";
  const selectedDuration   = form.watch("followUpDuration");
  const selectedPatient    = patients.find((p) => p.id === selectedPatientId);
  const patientType        = derivePatientType(selectedPatient?.date_of_birth);
  const showDuration       = Boolean(selectedReason);
  const visitReasonPreview = buildVisitReason(patientType, selectedReason, selectedDuration);

  const generateRecurringDates = (startDate: Date, endDate: Date, recurrenceType: RecurrenceType) => {
    const dates: Date[] = [];
    let currentDate = new Date(startDate);
    while (isBefore(currentDate, endDate)) {
      dates.push(new Date(currentDate));
      switch (recurrenceType) {
        case RECURRENCE_TYPES.WEEKLY:    currentDate = addWeeks(currentDate, 1); break;
        case RECURRENCE_TYPES.MONTHLY:   currentDate = addMonths(currentDate, 1); break;
        case RECURRENCE_TYPES.QUARTERLY: currentDate = addQuarters(currentDate, 1); break;
        default:                         currentDate = addWeeks(currentDate, 1);
      }
    }
    return dates;
  };

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);

      const profile = await AuthService.getProfile();
      if (!profile) throw new Error("Profile not found");

      const [hours, minutes] = data.time.split(":").map(Number);
      const d = data.date;
      const startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);

      // Use clinic-local now for the past-appointment check.
      // If the location or timezone cannot be resolved, fall back to
      // browser local time — same behaviour as the original code.
      const selectedLoc = locations.find(l => String(l.value) === data.locationId);
      const clinicTz = selectedLoc?.stateName ? getTimezoneForState(selectedLoc.stateName) : null;
      const nowForComparison = clinicTz ? getNowInTimezone(clinicTz) : new Date();

      if (startDate <= nowForComparison) {
        toast.error("Cannot create an appointment in the past. Please select a future date and time.");
        setIsLoading(false);
        return;
      }

      const defaultDurationMap: Record<string, number> = {
        "Initial": 60,
        "Follow-up": 20,
        "Internal care transfer": 40,
        "Initial Therapy": 60,
      };

      const durationMinutes =
        data.followUpDuration ||
        defaultDurationMap[data.reason] ||
        30;

      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + durationMinutes);

      const visitReason = buildVisitReason(patientType, data.reason as AppointmentReason, durationMinutes);

      const buildEncounter = (encounterStart: Date) => {
        const encounterEnd = new Date(encounterStart);
        encounterEnd.setMinutes(encounterEnd.getMinutes() + durationMinutes);
        return {
          patient_id:               data.patientId,
          primary_provider_id:      profile.id,
          encounter_date:           format(encounterStart, "yyyy-MM-dd"),
          encounter_type:           data.type,
          status:                   "scheduled",
          start_time:               encounterStart.toISOString(),
          end_time:                 encounterEnd.toISOString(),
          visit_reason:             visitReason,
          visit_priority:           "routine",
          clinical_notes:           data.notes || null,
          encounter_review_status:  "pending",
          location_ids:             [Number(data.locationId)],
          duration_minutes:         durationMinutes,
          reason_label:             data.reason,
          created_at:               new Date().toISOString(),
          updated_at:               new Date().toISOString(),
          encounter_id:             uuidv4(),
        };
      };

      let appointmentsToCreate = [];

      if (data.isRecurring && data.recurrenceEndDate) {
        const appointmentDates = generateRecurringDates(startDate, data.recurrenceEndDate, data.recurrenceType);
        appointmentsToCreate = appointmentDates.map((date) => {
          const encounterStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
          return buildEncounter(encounterStart);
        });
      } else {
        appointmentsToCreate = [buildEncounter(startDate)];
      }

      const res = await AuthService.createAppointments(appointmentsToCreate);
      if (res.error) throw new Error(res.error);

      toast.success(
        data.isRecurring
          ? "Recurring appointments created — patient has been notified via SMS and email."
          : "Appointment created — patient has been notified via SMS and email."
      );

      if (res.notification_warning) {
        toast.warning(`Patient notification issue: ${res.notification_warning}`, { duration: 8000 });
      }

      form.reset();
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error creating appointment:", error);
      toast.error(error.message || "Failed to create appointment");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 overflow-visible">
        <div className="grid grid-cols-1 gap-4">

          {/* ── Patient Picker ─────────────────────────────────────────── */}
          <FormField
            control={form.control}
            name="patientId"
            render={({ field }) => (
              <FormItem className="flex flex-col space-y-2">
                <FormLabel>Select Patient</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between"
                        disabled={isLoadingPatients}
                      >
                        {field.value && selectedPatient ? (
                          <span className="truncate">
                            {`${selectedPatient.first_name} ${selectedPatient.last_name}${selectedPatient.mobile_number ? ` (${formatPhoneDisplay(selectedPatient.mobile_number)})` : ""}`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Select patient...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-2" align="start">
                    <div className="space-y-2">
                      <Input
                        placeholder="Search patients..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                      />
                      {isLoadingPatients || isSearching ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : (
                        <div className="max-h-[300px] overflow-y-auto space-y-1">
                          {patients.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery ? "No patients found" : "No patients available"}
                            </div>
                          ) : (
                            patients.map((patient) => (
                              <Button
                                key={patient.id}
                                type="button"
                                variant="ghost"
                                className={`w-full justify-start h-auto py-2 px-3 text-left ${patient.id === field.value ? "bg-accent" : ""}`}
                                onClick={() => {
                                  form.setValue("patientId", patient.id);
                                  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${patient.id === field.value ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col">
                                  <span>{patient.first_name} {patient.last_name}</span>
                                  {patient.mobile_number && (
                                    <span className="text-xs text-muted-foreground">
                                      {formatPhoneDisplay(patient.mobile_number)}
                                    </span>
                                  )}
                                </div>
                              </Button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Patient Type Badge ─────────────────────────────────────── */}
          {selectedPatient && patientType && (
            <div className="flex items-center gap-2 -mt-1 px-0.5">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide border",
                  patientType === "Adult"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                )}
              >
                {patientType === "Adult" ? "🔵" : "🟡"} {patientType}
              </span>
              {selectedPatient.date_of_birth && (
                <span className="text-xs text-muted-foreground">
                  DOB: {format(new Date(selectedPatient.date_of_birth), "MM/dd/yyyy")}
                </span>
              )}
            </div>
          )}

          {/* ── Date ──────────────────────────────────────────────────── */}
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Appointment Date</FormLabel>
                <Popover modal={true} open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn("pl-3 text-left font-normal w-full justify-start", !field.value && "text-muted-foreground")}
                      >
                        {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => { field.onChange(date); setDatePopoverOpen(false); }}
                      disabled={(date) => date <= startOfToday()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Time ──────────────────────────────────────────────────── */}
          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Appointment Mode (Telehealth vs In-Person) ─────────────── */}
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-sm font-semibold text-gray-700">
                  Appointment Mode
                </FormLabel>
                <div className="grid grid-cols-2 gap-3">
                  {/* Telehealth Card */}
                  <button
                    type="button"
                    onClick={() => field.onChange("TeleHealth")}
                    className={cn(
                      "flex flex-col items-start p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer",
                      field.value === "TeleHealth"
                        ? "border-indigo-600 bg-indigo-50/70 shadow-sm ring-1 ring-indigo-500"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn(
                        "p-1.5 rounded-lg",
                        field.value === "TeleHealth" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"
                      )}>
                        <Video className="h-4 w-4" />
                      </div>
                      <span className={cn(
                        "font-bold text-sm",
                        field.value === "TeleHealth" ? "text-indigo-900" : "text-gray-800"
                      )}>
                        Telehealth
                      </span>
                    </div>
                    <span className="text-[11px] text-gray-500 leading-tight">
                      Virtual / Phone consultation with patient
                    </span>
                  </button>

                  {/* In-Person Card */}
                  <button
                    type="button"
                    onClick={() => field.onChange("InPerson")}
                    className={cn(
                      "flex flex-col items-start p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer",
                      field.value === "InPerson"
                        ? "border-emerald-600 bg-emerald-50/70 shadow-sm ring-1 ring-emerald-500"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn(
                        "p-1.5 rounded-lg",
                        field.value === "InPerson" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600"
                      )}>
                        <Building2 className="h-4 w-4" />
                      </div>
                      <span className={cn(
                        "font-bold text-sm",
                        field.value === "InPerson" ? "text-emerald-900" : "text-gray-800"
                      )}>
                        In-Person
                      </span>
                    </div>
                    <span className="text-[11px] text-gray-500 leading-tight">
                      Clinic visit at physical facility location
                    </span>
                  </button>
                </div>

                {/* Optional other types if defined in DB */}
                {appointmentTypes.filter(t => t.value !== "TeleHealth" && t.value !== "InPerson").length > 0 && (
                  <div className="pt-1">
                    <Select
                      value={field.value !== "TeleHealth" && field.value !== "InPerson" ? field.value : ""}
                      onValueChange={(val) => { if (val) field.onChange(val); }}
                    >
                      <SelectTrigger className="h-9 text-xs text-gray-500 bg-gray-50 border-gray-200">
                        <SelectValue placeholder="Or select specialized mode (BHI / Survey)..." />
                      </SelectTrigger>
                      <SelectContent>
                        {appointmentTypes
                          .filter(t => t.value !== "TeleHealth" && t.value !== "InPerson")
                          .map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Appointment Reason ─────────────────────────────────────── */}
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Appointment Reason</FormLabel>
                <Select
                  onValueChange={(val) => {
                    field.onChange(val);
                    const defaultDurations: Record<string, number> = {
                      "Initial": 60,
                      "Follow-up": 20,
                      "Internal care transfer": 40,
                      "Initial Therapy": 60,
                    };
                    if (defaultDurations[val]) {
                      form.setValue("followUpDuration", defaultDurations[val]);
                    }
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select appointment reason" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {APPOINTMENT_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Duration (Follow-up / Initial Therapy only) ────────────── */}
          {showDuration && (
            <FormField
              control={form.control}
              name="followUpDuration"
              render={({ field }) => (
                <FormItem
                  className="rounded-xl border border-dashed border-blue-200 bg-blue-50/50 px-4 py-3 space-y-1.5 transition-all"
                >
                  <FormLabel className="flex items-center gap-1.5 text-blue-700 text-sm font-semibold">
                    <Clock className="w-3.5 h-3.5" />
                    Session Duration
                  </FormLabel>
                  <Select
                    onValueChange={(val) => field.onChange(Number(val))}
                    value={field.value ? String(field.value) : ""}
                  >
                    <FormControl>
                      <SelectTrigger className="bg-white border-blue-200 focus:ring-blue-300">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {FOLLOWUP_DURATIONS.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* ── Visit Reason Preview ───────────────────────────────────── */}
          {visitReasonPreview && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
              <span className="text-xs font-medium text-green-600 shrink-0">Saved as:</span>
              <span className="text-xs font-bold text-green-800 tracking-wide">{visitReasonPreview}</span>
            </div>
          )}

          {/* ── Hospital + Branch ──────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={form.control}
              name="hospitalName"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm font-semibold text-gray-700">Hospital</FormLabel>
                  <Select onValueChange={handleHospitalChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white/50 focus:ring-green-500/20 transition-all">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <SelectValue placeholder="Select hospital" />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                      {hospitals.map((h) => (
                        <SelectItem key={h} value={h} className="rounded-lg focus:bg-green-50">{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[10px]" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="locationId"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm font-semibold text-gray-700">Branch</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={!form.watch("hospitalName")}>
                    <FormControl>
                      <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white/50 focus:ring-green-500/20 transition-all overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                          <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="truncate flex-1 text-left text-sm">
                            <SelectValue placeholder={!form.watch("hospitalName") ? "Select hospital first" : "Select branch"} />
                          </span>
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                      {filteredLocations.map((loc) => (
                        <SelectItem key={loc.value} value={String(loc.value)} className="rounded-lg focus:bg-green-50">
                          <span>{loc.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[10px]" />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* ── Recurring ─────────────────────────────────────────────────── */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium flex items-center">
              <RefreshCw className="h-4 w-4 mr-2" />
              Recurring Appointment
            </h3>
            <FormField
              control={form.control}
              name="isRecurring"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <div className="flex items-center">
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="recurring-toggle"
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="sr-only"
                        />
                        <label
                          htmlFor="recurring-toggle"
                          className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${field.value ? "bg-primary" : "bg-gray-300"}`}
                        >
                          <span className={`block h-6 w-6 rounded-full bg-white shadow-md transform transition-transform duration-200 ease-in-out ${field.value ? "translate-x-4" : "translate-x-0"}`} />
                        </label>
                      </div>
                      <span className="text-sm text-gray-700">{field.value ? "On" : "Off"}</span>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {form.watch("isRecurring") && (
            <div className="pl-6 space-y-4">
              <FormField
                control={form.control}
                name="recurrenceType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recurrence Pattern</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select recurrence pattern" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={RECURRENCE_TYPES.WEEKLY}>Weekly</SelectItem>
                        <SelectItem value={RECURRENCE_TYPES.MONTHLY}>Monthly</SelectItem>
                        <SelectItem value={RECURRENCE_TYPES.QUARTERLY}>Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="recurrenceEndDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Recurrence End Date</FormLabel>
                    <Popover modal={true} open={endDatePopoverOpen} onOpenChange={setEndDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn("pl-3 text-left font-normal w-full justify-start", !field.value && "text-muted-foreground")}
                          >
                            {field.value ? format(field.value, "PPP") : <span>Pick an end date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => { field.onChange(date); setEndDatePopoverOpen(false); }}
                          disabled={(date) => date <= form.getValues("date")}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("recurrenceEndDate") && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-sm">
                    {form.watch("recurrenceType") === RECURRENCE_TYPES.WEEKLY && (
                      <>Appointments will be created <strong>weekly</strong> on {format(form.getValues("date"), "EEEE")}s at {form.getValues("time")} until {format(form.getValues("recurrenceEndDate")!, "PPP")}</>
                    )}
                    {form.watch("recurrenceType") === RECURRENCE_TYPES.MONTHLY && (
                      <>Appointments will be created <strong>monthly</strong> on the {form.getValues("date").getDate()}{getOrdinalSuffix(form.getValues("date").getDate())} at {form.getValues("time")} until {format(form.getValues("recurrenceEndDate")!, "PPP")}</>
                    )}
                    {form.watch("recurrenceType") === RECURRENCE_TYPES.QUARTERLY && (
                      <>Appointments will be created <strong>quarterly</strong> on {format(form.getValues("date"), "MMMM do")} at {form.getValues("time")} until {format(form.getValues("recurrenceEndDate")!, "PPP")}</>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Notes ─────────────────────────────────────────────────────── */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea placeholder="Add any additional notes..." className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={() => form.reset()} disabled={isLoading}>
            Reset
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Appointment"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export type { Patient };
